<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-6">My Payments</h1>
      </v-col>
    </v-row>

    <!-- Auto Pay Section (Dashboard API only) -->
    <v-row v-if="useDashboardApi && !loading">
      <v-col cols="12">
        <v-card class="pa-4" elevation="2">
          <v-card-title class="subtitle-1">Auto Pay</v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="6">
                <div v-if="enrollment.autoPay && enrollment.autoPay.hasCard" class="d-flex align-center">
                  <v-icon class="mr-2">mdi-credit-card</v-icon>
                  <span v-if="enrollment.autoPay.cardLast4">
                    {{ enrollment.autoPay.cardType || 'Card' }} ending in {{ enrollment.autoPay.cardLast4 }}
                  </span>
                  <span v-else>Card on file</span>
                </div>
                <div v-else class="grey--text">No card on file</div>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <!--
                  Auto Pay can be turned ON here, never off. It is part of the membership, and
                  the only thing that ends it is cancelling the plan — so once it is active
                  there is no switch to flip, and the card is updated instead. The dashboard
                  API refuses a disable outright (AUTO_PAY_DISABLE_ADMIN_ONLY); showing a
                  switch that always errors would be worse than showing none.
                -->
                <v-chip v-if="autoPayEnabled" small color="success" dark class="mr-4">
                  <v-icon left small>mdi-autorenew</v-icon>
                  Auto Pay active
                </v-chip>
                <v-switch
                  v-else
                  v-model="autoPayEnabled"
                  :loading="autoPayLoading"
                  label="Turn on Auto Pay"
                  :disabled="!(enrollment.autoPay && enrollment.autoPay.hasCard)"
                  @change="toggleAutoPay"
                />
                <v-btn
                  color="primary"
                  outlined
                  small
                  :class="autoPayEnabled ? '' : 'ml-4'"
                  @click="openAddCardDialog"
                >
                  {{ (enrollment.autoPay && enrollment.autoPay.hasCard) ? 'Update Card' : 'Add Card' }}
                </v-btn>
              </v-col>
              <v-col v-if="autoPayEnabled" cols="12" class="pt-0">
                <div class="text-caption grey--text">
                  Auto Pay is part of your membership. To change how your plan is paid, please
                  contact Family Health Choice.
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Summary Cards -->
    <v-row v-if="!loading">
      <v-col cols="12" md="4">
        <v-card class="pa-4" elevation="2">
          <div class="text-caption grey--text">Total Paid This Year</div>
          <div class="text-h4 success--text font-weight-bold mt-2">
            ${{ summary.totalPaidThisYear.toFixed(2) }}
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card class="pa-4" elevation="2">
          <div class="text-caption grey--text">Pending Payments</div>
          <div class="text-h4 warning--text font-weight-bold mt-2">
            ${{ summary.totalPending.toFixed(2) }}
          </div>
          <div
            v-if="useDashboardApi && summary.pendingNominalTotal != null && summary.pendingNominalTotal > summary.totalPending + 0.001"
            class="text-caption grey--text mt-1"
          >
            Plan total ${{ summary.pendingNominalTotal.toFixed(2) }} before credit
          </div>
          <div class="text-caption mt-1">{{ summary.pendingCount }} payment(s)</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card class="pa-4" elevation="2">
          <div class="text-caption grey--text">Next Payment</div>
          <div v-if="summary.nextPayment" class="mt-2">
            <div class="text-h4 primary--text font-weight-bold">
              ${{ formatAmount(summary.nextPayment.amount || summary.nextPayment.AMOUNT) }}
            </div>
            <div
              v-if="
                summary.nextPayment.nominalAmount != null &&
                  summary.nextPayment.nominalAmount > (summary.nextPayment.amount || summary.nextPayment.AMOUNT || 0) + 0.001
              "
              class="text-caption grey--text mt-1"
            >
              Plan ${{ formatAmount(summary.nextPayment.nominalAmount) }}/mo before credit
            </div>
            <div class="text-caption mt-1">
              Due: {{ formatDate(summary.nextPayment.dueDate || summary.nextPayment.DUE_DATE) }}
            </div>
          </div>
          <div v-else class="text-body-2 mt-2">No upcoming payments</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row class="mt-4">
      <v-col cols="12" md="3">
        <v-select
          v-model="statusFilter"
          :items="statusItems"
          label="Filter by Status"
          clearable
          outlined
          dense
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="yearFilter"
          :items="availableYears"
          label="Filter by Year"
          clearable
          outlined
          dense
        />
      </v-col>
    </v-row>

    <!-- Payments/Schedules Table -->
    <v-row>
      <v-col cols="12">
        <v-card flat>
          <v-card-text>
            <v-data-table
              :loading="loading"
              :headers="tableHeaders"
              :items="filteredPayments"
              :items-per-page="12"
              :footer-props="{ 'items-per-page-options': [12, 24, 50, -1] }"
              :sort-by="useDashboardApi ? 'dueDate' : 'DUE_DATE'"
              :sort-desc="false"
              loading-text="Loading payments..."
              no-data-text="No payments found"
              class="elevation-0"
            >
              <template v-slot:item.dueDate="{ item }">
                <template v-if="useDashboardApi">
                  <div>
                    {{ formatDate(item.dueDate) }}
                    <v-chip v-if="item.status === 'Overdue'" x-small color="error" class="ml-2">Overdue</v-chip>
                  </div>
                </template>
                <template v-else>
                  <div>
                    {{ formatDate(item.DUE_DATE) }}
                    <v-chip v-if="isOverdue(item)" x-small color="error" class="ml-2">Overdue</v-chip>
                  </div>
                </template>
              </template>
              <template v-slot:item.DUE_DATE="{ item }">
                <div>
                  {{ formatDate(item.DUE_DATE) }}
                  <v-chip v-if="isOverdue(item)" x-small color="error" class="ml-2">Overdue</v-chip>
                </div>
              </template>
              <template v-slot:item.amount="{ item }">
                <template v-if="useDashboardApi && scheduleLineIsCreditLedger(item)">
                  <div>
                    <span class="font-weight-bold success--text">−${{ formatAmount(Math.abs(item.amount || 0)) }}</span>
                    <div class="text-caption grey--text">Account credit</div>
                  </div>
                </template>
                <template
                  v-else-if="useDashboardApi && scheduleLineUsesBillingCreditEstimate(item)"
                >
                  <div>
                    <span class="font-weight-bold">${{ formatAmount(estimatedCardChargeForSchedule(item)) }}</span>
                    <div
                      v-if="creditAppliedPreviewAmount(item) > 0"
                      class="text-caption grey--text"
                    >
                      Plan ${{ formatAmount(item.amount) }}, −${{ formatAmount(creditAppliedPreviewAmount(item)) }} credit
                    </div>
                  </div>
                </template>
                <span v-else class="font-weight-bold">${{ formatAmount(item.amount) }}</span>
              </template>
              <template v-slot:item.AMOUNT="{ item }">
                <template v-if="mongoPaymentIsCredit(item)">
                  <div>
                    <span class="font-weight-bold success--text">−${{ formatAmount(Math.abs(item.AMOUNT || 0)) }}</span>
                    <div class="text-caption grey--text">Account credit</div>
                  </div>
                </template>
                <span v-else class="font-weight-bold">${{ formatAmount(item.AMOUNT) }}</span>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip :color="getStatusColor(item.status)" small>
                  {{ item.status }}
                </v-chip>
              </template>
              <template v-slot:item.STATUS="{ item }">
                <v-chip :color="getStatusColor(item.STATUS)" small>
                  {{ item.STATUS }}
                </v-chip>
              </template>
              <template v-slot:item.paidAt="{ item }">
                {{ item.paidAt ? formatDate(item.paidAt) : '-' }}
              </template>
              <template v-slot:item.PAYMENT_DATE="{ item }">
                {{ item.PAYMENT_DATE ? formatDate(item.PAYMENT_DATE) : '-' }}
              </template>
              <template v-slot:item.transactionId="{ item }">
                {{ item.transactionId || '-' }}
              </template>
              <template v-slot:item.PAYMENT_METHOD="{ item }">
                {{ item.PAYMENT_METHOD || '-' }}
              </template>
              <template v-slot:item.REFERENCE_NUMBER="{ item }">
                {{ item.REFERENCE_NUMBER || '-' }}
              </template>
              <template v-slot:item.actions="{ item }" v-if="useDashboardApi">
                <v-btn
                  v-if="item.status === 'Pending' || item.status === 'Overdue'"
                  color="primary"
                  small
                  @click="openPayDialog(item)"
                >
                  Pay
                </v-btn>
                <v-btn
                  v-if="item.status === 'Paid' && item._id"
                  icon
                  small
                  title="Download Receipt (PDF)"
                  :loading="downloadingReceiptId === item._id"
                  @click="downloadReceipt(item._id)"
                >
                  <v-icon small>mdi-file-download-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pay Dialog -->
    <v-dialog v-model="payDialog" max-width="560" persistent content-class="payment-dialog" transition="dialog-bottom-transition">
      <v-card class="payment-modal-card">
        <div class="payment-modal-header">
          <div class="payment-modal-header-content">
            <v-icon color="primary" class="mr-3">mdi-credit-card-check</v-icon>
            <div>
              <h2 class="payment-modal-title">Complete Payment</h2>
              <p class="payment-modal-subtitle">
                <template v-if="selectedSchedule">
                  ${{ formatAmount(selectedPayCardAmount) }} due
                  <span
                    v-if="selectedPayCreditApplied > 0"
                    class="d-block grey--text text-caption mt-1 font-weight-normal"
                  >
                    Monthly ${{ formatAmount(selectedSchedule.amount) }}, credit −${{ formatAmount(selectedPayCreditApplied) }}
                  </span>
                  <span
                    v-if="selectedPayCreditApplied > 0"
                    class="d-block grey--text text-caption mt-1 font-weight-normal"
                  >
                    Final card charge depends on your credit balance at payment time.
                  </span>
                </template>
              </p>
            </div>
          </div>
          <v-btn icon text @click="payDialog = false" aria-label="Close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider />
        <v-card-text class="payment-modal-body">
          <PaymentForm
            v-if="payDialog"
            ref="payForm"
            mode="pay"
            :amount="selectedPayCardAmount"
            :user="user"
            :profile="memberProfile"
            :submitting="paySubmitting"
            @success="onPaySuccess"
            @cancel="payDialog = false"
            @error="onPaymentError"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add Card Dialog -->
    <v-dialog v-model="addCardDialog" max-width="560" persistent content-class="payment-dialog" transition="dialog-bottom-transition">
      <v-card class="payment-modal-card">
        <div class="payment-modal-header">
          <div class="payment-modal-header-content">
            <v-icon color="primary" class="mr-3">mdi-credit-card-plus</v-icon>
            <div>
              <h2 class="payment-modal-title">Add Payment Method</h2>
              <p class="payment-modal-subtitle">Save your card for Auto Pay</p>
            </div>
          </div>
          <v-btn icon text @click="addCardDialog = false" aria-label="Close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider />
        <v-card-text class="payment-modal-body">
          <PaymentForm
            v-if="addCardDialog"
            ref="addCardForm"
            mode="add-card"
            :submitting="addCardSubmitting"
            :user="user"
            :profile="memberProfile"
            @success="onAddCardSuccess"
            @cancel="addCardDialog = false"
            @error="onPaymentError"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import PaymentForm from '@/components/memberPayments/PaymentForm.vue';

export default {
  name: 'MemberPayments',
  components: { PaymentForm },
  data() {
    return {
      user: null,
      loading: false,
      useDashboardApi: false,
      payments: [],
      schedules: [],
      enrollment: {},
      summary: {
        totalPaidThisYear: 0,
        totalPending: 0,
        pendingNominalTotal: null,
        pendingCount: 0,
        nextPayment: null
      },
      statusFilter: null,
      yearFilter: null,
      statusItems: ['All', 'Pending', 'Paid', 'Overdue', 'Cancelled'],
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      payDialog: false,
      addCardDialog: false,
      paySubmitting: false,
      addCardSubmitting: false,
      selectedSchedule: null,
      autoPayEnabled: false,
      autoPayLoading: false,
      memberProfile: null,
      /** Snapshot from Dashboard GET /schedules (≥ 0; used if line omits estimatedCardCharge) */
      billingCreditBalance: 0,
      downloadingReceiptId: null,
    };
  },
  watch: {
    payDialog(val) {
      if (val) this.fetchMemberProfile();
    },
    addCardDialog(val) {
      if (val) this.fetchMemberProfile();
    }
  },
  computed: {
    tableHeaders() {
      if (this.useDashboardApi) {
        return [
          { text: 'Due Date', value: 'dueDate', sortable: true },
          { text: 'Amount', value: 'amount', sortable: true },
          { text: 'Status', value: 'status', sortable: true },
          { text: 'Paid At', value: 'paidAt', sortable: true },
          { text: 'Transaction', value: 'transactionId', sortable: false },
          { text: 'Actions', value: 'actions', sortable: false }
        ];
      }
      return [
        { text: 'Due Date', value: 'DUE_DATE', sortable: true },
        { text: 'Amount', value: 'AMOUNT', sortable: true },
        { text: 'Status', value: 'STATUS', sortable: true },
        { text: 'Payment Date', value: 'PAYMENT_DATE', sortable: true },
        { text: 'Payment Method', value: 'PAYMENT_METHOD', sortable: false },
        { text: 'Reference', value: 'REFERENCE_NUMBER', sortable: false }
      ];
    },
    filteredPayments() {
      const items = this.useDashboardApi ? this.schedules : this.payments;

      let filtered = items;

      if (this.statusFilter && this.statusFilter !== 'All') {
        if (this.useDashboardApi) {
          filtered = filtered.filter(p => p.status === this.statusFilter);
        } else {
          filtered = filtered.filter(p => p.STATUS === this.statusFilter);
        }
      }

      if (this.yearFilter) {
        if (this.useDashboardApi) {
          filtered = filtered.filter(p => {
            const d = p.dueDate || p.dueMonth;
            const year = d ? new Date(d).getFullYear() : null;
            return year === this.yearFilter;
          });
        } else {
          filtered = filtered.filter(p => {
            const year = new Date(p.DUE_DATE).getFullYear();
            return year === this.yearFilter;
          });
        }
      }

      return filtered;
    },
    availableYears() {
      const items = this.useDashboardApi ? this.schedules : this.payments;
      if (items.length === 0) return [];

      const years = items.map(p => {
        const d = this.useDashboardApi ? (p.dueDate || p.dueMonth) : p.DUE_DATE;
        return new Date(d).getFullYear();
      });
      const uniqueYears = [...new Set(years)].filter(Boolean).sort((a, b) => b - a);
      return uniqueYears;
    },

    selectedPayCardAmount() {
      if (!this.selectedSchedule) return 0;
      return this.estimatedCardChargeForSchedule(this.selectedSchedule);
    },

    selectedPayCreditApplied() {
      if (!this.selectedSchedule) return 0;
      const due = this.roundMoneyAmount(this.selectedSchedule.amount);
      return this.roundMoneyAmount(due - this.selectedPayCardAmount);
    }
  },
  methods: {
    async downloadReceipt(scheduleId) {
      this.downloadingReceiptId = scheduleId;
      try {
        const response = await axios.get(`/api/member-payments/schedules/${scheduleId}/receipt.pdf`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${scheduleId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.snackbarMessage = 'Could not download receipt. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.downloadingReceiptId = null;
      }
    },
    async getUser() {
      try {
        const response = await axios.get('/api/user');
        this.user = response.data[0];

        if (this.user.role !== 'Member') {
          this.$router.push('/');
          return;
        }

        const availableRes = await axios.get('/api/member-payments/available').catch(() => ({ data: { available: false } }));
        this.useDashboardApi = availableRes.data && availableRes.data.available;

        if (this.useDashboardApi) {
          await this.loadDashboardData();
        } else {
          const insurance = this.getInsurance();
          await this.getPayments(insurance);
          await this.getSummary(insurance);
        }
      } catch (error) {
        console.error('Error getting user:', error);
        this.$router.push('/login');
      }
    },

    async loadDashboardData() {
      this.loading = true;
      try {
        const [enrollmentRes, schedulesRes] = await Promise.all([
          axios.get('/api/member-payments/enrollment'),
          axios.get('/api/member-payments/schedules')
        ]);

        if (enrollmentRes.data && enrollmentRes.data.success) {
          this.enrollment = enrollmentRes.data;
          this.autoPayEnabled = enrollmentRes.data.autoPay && enrollmentRes.data.autoPay.enabled;
        }

        if (schedulesRes.data && schedulesRes.data.success) {
          this.applySchedulesPayload(schedulesRes.data);
        }

        const schedRoot = schedulesRes.data && schedulesRes.data.success ? schedulesRes.data : null;
        const hasScheduleBalance =
          schedRoot &&
          schedRoot.billingCreditBalance != null &&
          schedRoot.billingCreditBalance !== '';
        if (
          !hasScheduleBalance &&
          enrollmentRes.data &&
          enrollmentRes.data.success &&
          enrollmentRes.data.billingCreditBalance != null &&
          enrollmentRes.data.billingCreditBalance !== ''
        ) {
          const b =
            typeof enrollmentRes.data.billingCreditBalance === 'number'
              ? enrollmentRes.data.billingCreditBalance
              : parseFloat(enrollmentRes.data.billingCreditBalance);
          if (!Number.isNaN(b)) this.billingCreditBalance = this.roundMoneyAmount(b);
        }

        this.computeSummaryFromSchedules();

        // Fallback: se Dashboard retornou vazio mas o membro pode ter pagamentos locais
        if (this.schedules.length === 0) {
          const insurance = this.getInsurance();
          await this.getPayments(insurance);
          await this.getSummary(insurance);
          if (this.payments.length > 0) {
            this.useDashboardApi = false;
          }
        }
      } catch (error) {
        console.error('Error loading dashboard payments:', error);
        this.showSnackbar('Error loading payments', 'error');
        this.useDashboardApi = false;
        const insurance = this.getInsurance();
        await this.getPayments(insurance);
        await this.getSummary(insurance);
      } finally {
        this.loading = false;
      }
    },

    computeSummaryFromSchedules() {
      const currentYear = new Date().getFullYear();
      const paid = this.schedules.filter(p => p.status === 'Paid');
      const pending = this.schedules.filter(p => p.status === 'Pending' || p.status === 'Overdue');

      const totalPaidThisYear = this.roundMoneyAmount(
        paid
          .filter(p => new Date(p.paidAt || 0).getFullYear() === currentYear)
          .reduce((sum, p) => sum + this.signedPaidAmountForDashboardYear(p), 0)
      );

      const pendingNominalTotal = pending.reduce(
        (sum, p) => sum + this.roundMoneyAmount(p.amount || 0),
        0
      );
      const totalPending = pending.reduce(
        (sum, p) => sum + this.pendingCardChargeForDashboardSummary(p),
        0
      );

      const nextPayment = pending
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0] || null;

      const nextNominal = nextPayment ? this.roundMoneyAmount(nextPayment.amount || 0) : null;

      this.summary = {
        totalPaidThisYear,
        totalPending,
        pendingNominalTotal,
        pendingCount: pending.length,
        nextPayment: nextPayment
          ? {
            amount: this.pendingCardChargeForDashboardSummary(nextPayment),
            nominalAmount: nextNominal,
            dueDate: nextPayment.dueDate
          }
          : null
      };
    },

    getInsurance() {
      return this.user && this.user.insurance && this.user.insurance.length > 0
        ? this.user.insurance[0]
        : 'familyhealthchoice';
    },

    async getPayments(insurance) {
      this.loading = true;
      try {
        const response = await axios.get(`/api/enrollment/payments/${insurance}`);
        this.payments = response.data;
      } catch (error) {
        console.error('Error fetching payments:', error);
        this.showSnackbar('Error loading payments', 'error');
      } finally {
        this.loading = false;
      }
    },

    async getSummary(insurance) {
      try {
        const response = await axios.get(`/api/enrollment/payments/${insurance}/summary`);
        this.summary = response.data;
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    },

    async fetchMemberProfile() {
      try {
        const res = await axios.get('/api/member-payments/profile');
        this.memberProfile = res.data;
      } catch (err) {
        console.warn('Could not fetch member profile for address pre-fill:', err && err.message);
        this.memberProfile = null;
      }
    },

    roundMoneyAmount(n) {
      const x = typeof n === 'number' ? n : parseFloat(n);
      if (Number.isNaN(x)) return 0;
      return Math.round(x * 100) / 100;
    },

    /** Crédito no ledger (não é cobrança): ex. transactionId CREDIT-GRANT-* ou type billing_credit. */
    scheduleLineIsCreditLedger(schedule) {
      if (!schedule) return false;
      const tid = String(schedule.transactionId || '').trim();
      if (/^credit/i.test(tid)) return true;
      const t = String(schedule.type || schedule.lineType || '');
      return /billing_credit/i.test(t);
    },

    /** Valor com sinal para totais anuais: créditos reduzem o "total paid" líquido. */
    signedPaidAmountForDashboardYear(p) {
      const a = this.roundMoneyAmount(p.amount || 0);
      if (this.scheduleLineIsCreditLedger(p)) return -Math.abs(a);
      return a;
    },

    mongoPaymentIsCredit(payment) {
      if (!payment) return false;
      const ref = String(payment.REFERENCE_NUMBER || '').trim();
      const pid = String(payment.PAYMENT_ID || '').trim();
      return /^credit/i.test(ref) || /^credit/i.test(pid);
    },

    /** Pending/Overdue lines that receive billing-credit estimates (exclude credit ledger rows). */
    scheduleLineUsesBillingCreditEstimate(schedule) {
      if (
        !schedule ||
        (schedule.status !== 'Pending' && schedule.status !== 'Overdue')
      ) {
        return false;
      }
      const t = String(schedule.type || schedule.lineType || '');
      return !/billing_credit/i.test(t);
    },

    pendingCardChargeForDashboardSummary(schedule) {
      if (!this.scheduleLineUsesBillingCreditEstimate(schedule)) {
        return this.roundMoneyAmount(schedule.amount || 0);
      }
      return this.estimatedCardChargeForSchedule(schedule);
    },

    creditAppliedPreviewAmount(schedule) {
      if (!this.scheduleLineUsesBillingCreditEstimate(schedule)) return 0;
      const due = this.roundMoneyAmount(schedule.amount);
      const card = this.estimatedCardChargeForSchedule(schedule);
      return this.roundMoneyAmount(due - card);
    },

    /**
     * Card charge for a schedule line: prefer API estimatedCardCharge; else match server cents math.
     */
    estimatedCardChargeForSchedule(schedule) {
      if (!schedule) return 0;
      const est = schedule.estimatedCardCharge;
      if (est !== undefined && est !== null && est !== '') {
        const parsed = typeof est === 'number' ? est : parseFloat(est);
        if (!Number.isNaN(parsed)) return this.roundMoneyAmount(parsed);
      }
      const balance = Math.max(0, this.roundMoneyAmount(this.billingCreditBalance));
      const due = this.roundMoneyAmount(schedule.amount);
      const applied = this.roundMoneyAmount(Math.min(balance, due));
      return this.roundMoneyAmount(due - applied);
    },

    applySchedulesPayload(data) {
      if (!data || !data.success) return;
      if (Array.isArray(data.schedules)) this.schedules = data.schedules;
      if (data.billingCreditBalance != null && data.billingCreditBalance !== '') {
        const b = typeof data.billingCreditBalance === 'number'
          ? data.billingCreditBalance
          : parseFloat(data.billingCreditBalance);
        this.billingCreditBalance = Number.isNaN(b) ? 0 : this.roundMoneyAmount(b);
      }
    },

    async openPayDialog(schedule) {
      this.selectedSchedule = schedule;
      this.payDialog = true;
      if (!this.useDashboardApi) return;
      try {
        const res = await axios.get('/api/member-payments/schedules');
        if (res.data && res.data.success) {
          this.applySchedulesPayload(res.data);
          const id = schedule._id;
          const refreshed = (res.data.schedules || []).find((s) => s._id === id);
          if (refreshed) this.selectedSchedule = refreshed;
          this.computeSummaryFromSchedules();
        }
      } catch (_) {
        /* keep prior snapshot */
      }
    },

    openAddCardDialog() {
      this.addCardDialog = true;
    },

    async onPaySuccess(payload) {
      if (!this.selectedSchedule) return;

      try {
        const chargeAmount = this.estimatedCardChargeForSchedule(this.selectedSchedule);
        const response = await axios.post(
          `/api/member-payments/schedule/${this.selectedSchedule._id}/pay`,
          {
            paymentToken: payload.paymentToken,
            amount: chargeAmount,
            billingInfo: payload.billingInfo,
            saveForAutoPay: payload.saveForAutoPay
          }
        );

        if (response.data && response.data.success) {
          this.showSnackbar(response.data.message || 'Payment successful');
          this.payDialog = false;
          this.selectedSchedule = null;
          await this.loadDashboardData();
          this.$root.$emit('refresh-member-billing-alert');
        } else {
          this.showSnackbar(response.data.error || response.data.details || 'Payment failed', 'error');
        }
      } catch (error) {
        const data = error.response && error.response.data;
        const code =
          (data && (data.code || data.error)) ||
          (typeof (data && data.message) === 'string' ? data.message : '');
        const isMismatch =
          code === 'AMOUNT_MISMATCH' ||
          (typeof code === 'string' && code.indexOf && code.indexOf('AMOUNT_MISMATCH') >= 0);
        if (isMismatch && this.useDashboardApi) {
          await this.loadDashboardData();
          this.showSnackbar(
            (data && (data.message || data.details)) ||
              'The amount due on your card was updated. Please review and try again.',
            'warning'
          );
        } else {
          const msg = data
            ? (data.error || data.details || data.message)
            : error.message;
          this.showSnackbar(msg || 'Payment failed', 'error');
        }
      } finally {
        this.paySubmitting = false;
      }
    },

    async onAddCardSuccess(payload) {
      if (this.addCardSubmitting) return;

      this.addCardSubmitting = true;
      try {
        const response = await axios.post('/api/member-payments/add-card', {
          paymentToken: payload.paymentToken,
          billingInfo: payload.billingInfo
        });

        if (response.data && response.data.success) {
          this.showSnackbar(response.data.message || 'Card saved');
          this.addCardDialog = false;
          await this.loadDashboardData();
        } else {
          this.showSnackbar(response.data.error || 'Failed to save card', 'error');
        }
      } catch (error) {
        const msg = error.response && error.response.data
          ? (error.response.data.error || error.response.data.details)
          : error.message;
        this.showSnackbar(msg || 'Failed to save card', 'error');
      } finally {
        this.addCardSubmitting = false;
      }
    },

    /**
     * Opt in to Auto Pay. There is no opt-out: the switch is only rendered while it is off,
     * and the dashboard API refuses a disable regardless of what is sent.
     */
    async toggleAutoPay() {
      this.autoPayLoading = true;
      try {
        const response = await axios.patch('/api/member-payments/auto-pay', {
          enabled: true
        });

        if (response.data && response.data.success) {
          this.enrollment.autoPay = this.enrollment.autoPay || {};
          this.enrollment.autoPay.enabled = response.data.autoPayEnabled;
          this.showSnackbar('Auto Pay updated');
        } else {
          this.autoPayEnabled = !this.autoPayEnabled;
          this.showSnackbar(response.data.error || response.data.details || 'Failed to update', 'error');
        }
      } catch (error) {
        this.autoPayEnabled = !this.autoPayEnabled;
        const msg = error.response && error.response.data
          ? (error.response.data.error || error.response.data.details)
          : error.message;
        this.showSnackbar(msg || 'Failed to update Auto Pay', 'error');
      } finally {
        this.autoPayLoading = false;
      }
    },

    onPaymentError(msg) {
      this.showSnackbar(msg || 'An error occurred', 'error');
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    },

    formatAmount(amt) {
      const n = parseFloat(amt);
      return isNaN(n) ? '0.00' : n.toFixed(2);
    },

    isOverdue(payment) {
      if (payment.STATUS === 'Paid' || payment.STATUS === 'Cancelled') return false;
      const today = new Date();
      const dueDate = new Date(payment.DUE_DATE);
      return dueDate < today;
    },

    getStatusColor(status) {
      const colors = {
        Pending: 'orange',
        Paid: 'green',
        Overdue: 'red',
        Cancelled: 'grey'
      };
      return colors[status] || 'grey';
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  },
  mounted() {
    this.getUser();
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}
</style>

<style>
.payment-dialog {
  align-items: flex-start;
  padding-top: 48px;
}
.payment-modal-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.12) !important;
}
.payment-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
}
.payment-modal-header-content {
  display: flex;
  align-items: center;
}
.payment-modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #1a1a2e;
}
.payment-modal-subtitle {
  font-size: 0.875rem;
  color: #6c757d;
  margin: 2px 0 0 0;
}
.payment-modal-body {
  padding: 24px !important;
  max-height: 70vh;
  overflow-y: auto;
}
</style>
